import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import type { EstimativaDoLote, LoteDeAnalise, ProgressoDoLote } from '../../types/loteDeAnalise'

/** As chaves do lote de análise. */
const chaves = {
  tudo: () => ['admin', 'lotes-de-analise'] as const,
  previa: (competencia: string) => ['admin', 'lotes-de-analise', 'previa', competencia] as const,
  atual: () => ['admin', 'lotes-de-analise', 'atual'] as const,
  ultimos: () => ['admin', 'lotes-de-analise', 'ultimos'] as const,
}

/**
 * A prévia de um mês, antes de confirmar.
 *
 * @param competencia mês `AAAA-MM`; vazio não consulta
 * @returns a consulta
 */
export function usePreviaDoLote(competencia: string) {
  return useQuery({
    queryKey: chaves.previa(competencia),
    enabled: competencia.length === 7,
    queryFn: async () =>
      (await api.get<EstimativaDoLote>('/admin/lotes-de-analise/previa', { params: { competencia } })).data,
  })
}

/**
 * O lote em andamento, se houver.
 *
 * **O poll para quando o lote encerra**, e não fica batendo no servidor para sempre: o dreno é de
 * uma rodada por minuto, e um refetch de cinco segundos contra um lote concluído é só ruído.
 *
 * @returns a consulta
 */
export function useLoteAtual() {
  return useQuery({
    queryKey: chaves.atual(),
    queryFn: async () => {
      // Sem lote aberto o servidor devolve corpo VAZIO, e não `null`: com
      // `default-property-inclusion: non_null`, um `null` no topo vira string vazia no axios.
      const dados = (await api.get<ProgressoDoLote | ''>('/admin/lotes-de-analise/atual')).data
      return dados && typeof dados === 'object' ? dados : null
    },
    refetchInterval: (consulta) => {
      const dados = consulta.state.data
      return dados && (dados.situacao === 'EM_ANDAMENTO' || dados.situacao === 'PAUSADO_NO_TETO') ? 5000 : false
    },
  })
}

/**
 * Os últimos lotes pedidos.
 *
 * @returns a consulta
 */
export function useUltimosLotes() {
  return useQuery({
    queryKey: chaves.ultimos(),
    queryFn: async () => (await api.get<LoteDeAnalise[]>('/admin/lotes-de-analise')).data,
  })
}

/**
 * Abre o lote, congelando o sujeito.
 *
 * @returns a mutação
 */
export function usePedirLote() {
  return useMutacaoDoLote((corpo: { competencia: string; justificativa: string }) =>
    api.post('/admin/lotes-de-analise', corpo),
  )
}

/**
 * Interrompe o lote. Os pareceres que já saíram ficam.
 *
 * @returns a mutação
 */
export function useInterromperLote() {
  return useMutacaoDoLote((corpo: { id: number; motivo: string }) =>
    api.post(`/admin/lotes-de-analise/${corpo.id}/parada`, { motivo: corpo.motivo }),
  )
}

/** Toda mutação de lote invalida a prévia, o atual e o histórico. */
function useMutacaoDoLote<T>(executar: (corpo: T) => Promise<unknown>) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: executar,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}
